// Google Analytics 4 Integration for INTERPACK A.Ş

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-XXXXXXXXXX';
const DEBUG_MODE = process.env.NODE_ENV === 'development';

let isInitialized = false;

/**
 * Initialize Google Analytics 4
 * Must be called once on app startup before any tracking
 */
export function initializeGA() {
  // Prevent double initialization
  if (isInitialized) {
    console.warn('[GA4] Already initialized');
    return;
  }

  // Check if script already exists
  const existingScript = document.querySelector(
    `script[src*="googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}"]`
  );

  if (existingScript) {
    isInitialized = true;
    return;
  }

  // Validate measurement ID
  if (!GA_MEASUREMENT_ID || GA_MEASUREMENT_ID === 'G-XXXXXXXXXX') {
    console.warn('[GA4] Not configured. Set REACT_APP_GA_MEASUREMENT_ID in .env');
    return;
  }

  // Load gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  
  // CRITICAL: Use arguments object, not spread operator
  window.gtag = function() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };

  // Configure Google Analytics
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    'send_page_view': false, // Disable automatic pageviews for SPAs
    'debug_mode': DEBUG_MODE,
    'transport_type': 'beacon', // Use sendBeacon API for reliability
  });

  isInitialized = true;

  if (DEBUG_MODE) {
    console.log('[GA4] Initialized with ID:', GA_MEASUREMENT_ID);
  }
}

/**
 * Track page views for SPA route changes
 * @param {string} path - The page path (e.g., '/urunler/oyuncak')
 * @param {string} title - The page title
 */
export function trackPageView(path, title) {
  if (!window.gtag) {
    console.warn('[GA4] Not initialized');
    return;
  }

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
    page_location: window.location.href,
  });

  if (DEBUG_MODE) {
    console.log('[GA4] Page view tracked:', { path, title });
  }
}

/**
 * Track custom events
 * @param {string} eventName - Name of the event (e.g., 'button_click')
 * @param {Object} eventParams - Optional event parameters
 */
export function trackEvent(eventName, eventParams = {}) {
  if (!window.gtag) {
    console.warn('[GA4] Not initialized');
    return;
  }

  window.gtag('event', eventName, eventParams);

  if (DEBUG_MODE) {
    console.log('[GA4] Event tracked:', eventName, eventParams);
  }
}

/**
 * Track form submissions
 * @param {string} formName - Name of the form
 * @param {boolean} success - Whether submission was successful
 */
export function trackFormSubmission(formName, success = true) {
  trackEvent('form_submission', {
    form_name: formName,
    submission_success: success,
  });
}

/**
 * Track button clicks
 * @param {string} buttonName - Name of the button
 * @param {string} location - Where the button is located
 */
export function trackButtonClick(buttonName, location = 'general') {
  trackEvent('button_click', {
    button_name: buttonName,
    button_location: location,
  });
}

/**
 * Track category views
 * @param {string} categoryName - Name of the category
 */
export function trackCategoryView(categoryName) {
  trackEvent('view_category', {
    category_name: categoryName,
  });
}
